import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { formatBytes } from "../../lib/formatBytes";
import { makeUrl, routes } from "../../lib/urls";
import { Text } from "../Text";
import { Treemap } from "../Treemap";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  buildStorageSegments,
  OTHER_KEY,
  type StorageRow,
} from "./storageSegments";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

export function StorageBySpace() {
  const [storageStats, storageQuery] = useQuery(
    queries.monitoringStorageBySpace()
  );
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const displayName = useSpaceDisplayName();

  const { chartRows, totalBytes } = useMemo(() => {
    const bytesBySpace = new Map<string, number>();
    for (const stat of storageStats ?? []) {
      bytesBySpace.set(stat.spaceId, stat.bytesUsed ?? 0);
    }

    const chartRows: StorageRow[] = [];
    for (const space of spaces ?? []) {
      if (space.mode === "personal") continue;
      const bytes = bytesBySpace.get(space.id) ?? 0;
      if (bytes <= 0) continue;
      chartRows.push({ id: space.id, label: displayName(space), bytes });
    }

    chartRows.sort(
      (a, b) => b.bytes - a.bytes || a.label.localeCompare(b.label)
    );

    const totalBytes = chartRows.reduce((sum, row) => sum + row.bytes, 0);

    return { chartRows, totalBytes };
  }, [storageStats, spaces, displayName]);

  const colorMap = useMemo(() => spaceColorMap(spaces ?? []), [spaces]);

  const segments = useMemo(
    () => buildStorageSegments(chartRows, totalBytes, colorMap),
    [chartRows, totalBytes, colorMap]
  );

  if (storageQuery.type !== "complete" || spacesQuery.type !== "complete") {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <PanelHeader>
        <Text variant="text-small-md">Storage by space</Text>
        <TotalRow>
          <Text variant="display-md">{formatBytes(totalBytes)}</Text>
        </TotalRow>
      </PanelHeader>

      {chartRows.length === 0 ? (
        <Treemap segments={[]} placeholder />
      ) : (
        <Treemap
          segments={segments}
          formatValue={formatBytes}
          segmentUrl={(segment) =>
            segment.key === OTHER_KEY
              ? undefined
              : makeUrl(routes.modal.storage, { spaceId: segment.key })
          }
        />
      )}
    </Panel>
  );
}
